/**
 * Guia do MGDLead — o índice (`treinamentos/guia`).
 *
 * Destaque com o progresso, a busca no guia inteiro e os capítulos em três grupos, na ordem
 * de leitura. A busca fica na URL (`?q=`) para o link "procure por X no guia" poder ser
 * mandado no WhatsApp e abrir já com o resultado.
 */
import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ArrowLeft, Search, SearchX } from 'lucide-react';
import { EmptyState, Input, normalizeSearch } from '@mgdlead/ui';

import { buscarNoGuia } from '../../../features/guia/busca';
import { CapituloCard } from '../../../features/guia/componentes';
import {
  CAPITULOS,
  GRUPOS_DO_GUIA,
  caminhoDoGuia,
} from '../../../features/guia/conteudo';
import { GuiaDestaque } from '../../../features/guia/GuiaDestaque';
import { useCapitulosLidos } from '../../../features/guia/progresso';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

/** Pinta de leve os termos achados — ajuda o olho a confirmar que é o trecho certo. */
export function Destacado({ texto, consulta }: { texto: string; consulta: string }) {
  const termos = normalizeSearch(consulta).split(' ').filter((t) => t.length > 1);
  if (termos.length === 0) return <>{texto}</>;
  const plano = normalizeSearch(texto);
  const marcas: boolean[] = new Array(texto.length).fill(false);
  for (const t of termos) {
    let i = plano.indexOf(t);
    while (i >= 0) {
      for (let k = i; k < i + t.length; k++) marcas[k] = true;
      i = plano.indexOf(t, i + t.length);
    }
  }
  const partes: { t: string; m: boolean }[] = [];
  for (let i = 0; i < texto.length; i++) {
    const ultima = partes[partes.length - 1];
    if (ultima && ultima.m === marcas[i]) ultima.t += texto[i];
    else partes.push({ t: texto[i] ?? '', m: marcas[i] ?? false });
  }
  return (
    <>
      {partes.map((p, i) =>
        p.m ? (
          <mark key={i} className="rounded-xs bg-primary-soft text-primary-text">
            {p.t}
          </mark>
        ) : (
          <span key={i}>{p.t}</span>
        ),
      )}
    </>
  );
}

export function BuscaDoGuia({
  valor,
  aoMudar,
  autoFocus = false,
}: {
  valor: string;
  aoMudar: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <Input
      type="search"
      search
      aria-label="Buscar no guia"
      placeholder="Buscar no guia: “evasão”, “token”, “sem origem”…"
      leadingIcon={<Search aria-hidden="true" />}
      value={valor}
      onChange={(e) => aoMudar(e.target.value)}
      autoFocus={autoFocus}
    />
  );
}

export function GuiaIndicePage() {
  const { workspace } = useWorkspace();
  useDocumentTitle('Guia do MGDLead');
  const { lidos } = useCapitulosLidos();
  const [params, setParams] = useSearchParams();
  const consulta = params.get('q') ?? '';
  const resultados = useMemo(() => buscarNoGuia(CAPITULOS, consulta), [consulta]);
  const numeroDe = (id: string) => CAPITULOS.findIndex((c) => c.id === id) + 1;

  const mudarConsulta = (v: string) => {
    const proximo = new URLSearchParams(params);
    if (v) proximo.set('q', v);
    else proximo.delete('q');
    setParams(proximo, { replace: true });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <Link
          to={`/w/${workspace.slug}/treinamentos`}
          className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft aria-hidden="true" className="size-4" />
          Treinamentos
        </Link>
        <GuiaDestaque slug={workspace.slug} lidos={lidos} mostrarIndice={false} />
      </div>

      <section aria-label="Busca no guia" className="flex flex-col gap-4">
        <div className="max-w-xl">
          <BuscaDoGuia valor={consulta} aoMudar={mudarConsulta} />
        </div>

        {consulta.trim().length > 0 ? (
          resultados.length === 0 ? (
            <div className="rounded-lg bg-surface">
              <EmptyState
                kind="no-data"
                icon={SearchX}
                title={`Nada no guia com “${consulta}”`}
                description="Tente outra palavra — o nome de um botão, de uma tela ou de um número que aparece no painel."
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-muted-foreground" aria-live="polite">
                {resultados.length === 1 ? '1 resultado' : `${resultados.length} resultados`}
              </p>
              <ul className="flex flex-col gap-2">
                {resultados.map((r) => (
                  <li key={`${r.capitulo.id}-${r.passo.id}`}>
                    <Link
                      to={caminhoDoGuia(workspace.slug, r.capitulo.id, r.passo.id)}
                      className="flex flex-col gap-1 rounded-lg bg-surface p-4 ring-1 ring-transparent hover:ring-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="font-mono text-2xs uppercase tracking-caps text-subtle-foreground">
                        {r.capitulo.titulo} › {r.secao.titulo}
                      </span>
                      <span className="text-md text-foreground">
                        <Destacado texto={r.passo.titulo} consulta={consulta} />
                      </span>
                      <span className="text-sm text-muted-foreground">
                        <Destacado texto={r.trecho} consulta={consulta} />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )
        ) : null}
      </section>

      {GRUPOS_DO_GUIA.map((grupo) => (
        <section key={grupo.id} aria-labelledby={`grupo-${grupo.id}`} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h2 id={`grupo-${grupo.id}`} className="text-lg">
              {grupo.titulo}
            </h2>
            <p className="text-sm text-muted-foreground">{grupo.descricao}</p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {grupo.capitulos.map((c) => (
              <li key={c.id}>
                <CapituloCard
                  capitulo={c}
                  numero={numeroDe(c.id)}
                  href={caminhoDoGuia(workspace.slug, c.id)}
                  lido={lidos.has(c.id)}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
